import React, { useEffect, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import 'pages/Contact/contact.css'
import AOS from 'aos';
import 'aos/dist/aos.css';
import contact from '../../images/contactus_images/contact.jpg';
import { NavLink } from 'react-router-dom';
import Swal from 'sweetalert2';
import { apiUrl } from '../../utils/apiBase';

// Production key (domains must be listed in Google reCAPTCHA admin)
const RECAPTCHA_SITE_KEY = '6Lda7XYqAAAAAA9wI26_P6V4r9o8pws3uRnM-_GL';
// Google's official test key — works on localhost without domain setup
const RECAPTCHA_TEST_SITE_KEY = '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI';

const isLocalhost =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1');

function Contact({ showBanner = true }) {

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    pincode: "",
    requirement: "",
    bestTimeToCall: "",
    hearAboutUs: "",
    services: [],
    termsAccepted: false,
  });

  // eslint-disable-next-line
  const [status, setStatus] = useState('');
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [recaptchaError, setRecaptchaError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    AOS.init({ duration: 1500, easing: 'ease-in-out' });
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleRecaptchaChange = (token) => {
    if (token) {
      setRecaptchaToken(token);
      setRecaptchaError(false);
    } else {
      setRecaptchaError(true);
    }
  };

  const handleCheckboxChange = (e) => {
    const { name, value, checked } = e.target;

    setFormData((prevData) => {
      if (name === "termsAccepted") {
        return { ...prevData, [name]: checked };
      } else {
        const updatedServices = checked
          ? [...prevData.services, value]
          : prevData.services.filter((service) => service !== value);

        return { ...prevData, services: updatedServices };
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
  
    if (!formData.email) {
      Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: 'Email is required!',
      });
      return;
    }
    if (!recaptchaToken) {
      Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: 'Please complete the reCAPTCHA!',
      });
      return;
    }
  
    // Set loading state
    setIsSubmitting(true);
  
    const dataToSend = {
      name: formData.name.trim(),
      email: formData.email.trim(),
      number: formData.phone.trim(),
      phone_number: formData.phone.trim(),
      subject: 'Inquiry about Services',
      message: formData.requirement.trim() || 'Contact form submission',
      city_state: formData.city.trim(),
      pincode: formData.pincode.trim(),
      address: formData.address.trim(),
      service_looking_for: formData.services.join(', '),
      requirements: formData.requirement.trim(),
      time_to_call: formData.bestTimeToCall,
      here_aboutus_from: formData.hearAboutUs,
    };

    // On public site always use same-origin /smtp_api/ (proxied → admin).
    const onPublicSite =
      typeof window !== 'undefined' &&
      /^(www\.)?anuyog\.co$/i.test(window.location.hostname);

    const endpoint = onPublicSite
      ? '/smtp_api/'
      : apiUrl('smtp_api/');

    try {
      Swal.fire({
        icon: 'info',
        title: 'Processing...',
        text: 'Please wait while we send your message.',
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const result = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(dataToSend),
      });

      if (result.ok) {
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Message sent successfully!',
        });
        setFormData({
          name: '',
          email: '',
          phone: '',
          address: '',
          city: '',
          pincode: '',
          requirement: '',
          bestTimeToCall: '',
          hearAboutUs: '',
          services: [],
          termsAccepted: false,
        });
        setRecaptchaToken(null);
      } else {
        let errText = 'Failed to send message. Please try again.';
        try {
          const errJson = await result.json();
          if (errJson?.message) errText = errJson.message;
        } catch (_) {
          errText = `Failed to send message (HTTP ${result.status}).`;
        }
        Swal.fire({
          icon: 'error',
          title: 'Failed!',
          text: errText,
        });
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text:
          error?.message
            ? `Could not reach server: ${error.message}`
            : 'An error occurred. Please try again later.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {showBanner && (
        <section className='contact-banner-section'>
          <div className="contact-banner">
            <img src={contact} alt="Contact Banner" />
          </div>
        </section>
      )}

      <section className='contact-section' id='contact'>
        <div className="container">
          <div className="contact">
            <div className="row justify-content-center">
              <div className="con-text col-md-6">
                <h1 className='mb-5'><strong>Contact Us</strong></h1>
                <form className="contact-form" onSubmit={handleSubmit}>
                  <div className="row">
                    <div className="col-sm-6 d-flex align-items-start flex-column">
                      <label htmlFor="name">Enter Your Name</label>
                      <input
                        type="text"
                        name="name"
                        className="form-control"
                        placeholder=" e.g. John Doe"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="col-sm-6 d-flex align-items-start flex-column">
                      <label htmlFor="email">Enter Your Email</label>
                      <input
                        type="email"
                        name="email"
                        className="form-control"
                        placeholder=" e.g. john.doe@example.com"
                        value={formData.email}
                        onChange={handleInputChange}
                        
                      />
                    </div>
                    <div className="col-sm-6 d-flex align-items-start flex-column">
                      <label htmlFor="phone">Enter Your Number</label>
                      <input
                        type="text"
                        name="phone"
                        className="form-control"
                        placeholder="e.g. +91 XXXXX XXXXX"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="col-sm-6 d-flex align-items-start flex-column">
                      <label htmlFor="city">Enter Your City & State</label>
                      <input
                        type="text"
                        name="city"
                        className="form-control"
                        placeholder=" e.g.,  Bangalore & Karnataka"
                        value={formData.city}
                        onChange={handleInputChange}
                        
                      />
                    </div>
                    <div className="col-sm-6 d-flex align-items-start flex-column">
                      <label htmlFor="pincode">Enter Your Pin Code</label>
                      <input
                        type="text"
                        name="pincode"
                        className="form-control"
                        placeholder="Enter Pin Code"
                        value={formData.pincode}
                        onChange={handleInputChange}
                        
                      />
                    </div>
                    <div className="col-md-12 d-flex align-items-start flex-column">
                      <label htmlFor="address">Enter Your Address</label>
                      <textarea
                        name="address"
                        cols="40"
                        rows="4"
                        className="form-control"
                        placeholder="Your Address"
                        value={formData.address}
                        onChange={handleInputChange}
                        
                      ></textarea>
                    </div>

                    <div className="looking_service col-md-12 mt-3 d-flex align-items-start flex-column">
                      <label>What services are you looking for?</label>
                      <div className="d-flex flex-wrap">
                        {["Product", "Consultancy", "Workshop", "Courses", "Collaborate with Us"].map((service) => (
                          <div key={service} className="form-check me-3">
                            <input
                              type="checkbox"
                              name="services"
                              value={service}
                              onChange={handleCheckboxChange}
                            />
                            <label className="form-check-label ms-3">{service}</label>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="col-md-12 mt-3 d-flex align-items-start flex-column">
                      <label>What specific requirement do you have?</label>
                      <textarea
                        name="requirement"
                        cols="40"
                        rows="4"
                        className="form-control"
                        placeholder="I want to book Natural Living Simplified Workshop and order 10 Kg of Wheat."
                        value={formData.requirement}
                        onChange={handleInputChange}
                        
                      ></textarea>
                    </div>

                    <div className="col-md-6 mt-3 d-flex align-items-start flex-column">
                      <label>When is the best time to call you?</label>
                      <select
                        name="bestTimeToCall"
                        className="form-control custom-dropdown"
                        value={formData.bestTimeToCall}
                        onChange={handleInputChange}
                        
                      >
                        <option value="">Select...</option>
                        <option value="Morning">Morning</option>
                        <option value="Afternoon">Afternoon</option>
                        <option value="Evening">Evening</option>
                      </select>
                    </div>

                    <div className="col-md-6 mt-3 d-flex align-items-start flex-column">
                      <label>How did you hear about us?</label>
                      <select
                        name="hearAboutUs"
                        className="form-control custom-dropdown"
                        value={formData.hearAboutUs}
                        onChange={handleInputChange}
                        
                      >
                        <option value="">Select...</option>
                        <option value="Social Media">Social Media</option>
                        <option value="Friends or Family">Friends / Family</option>
                        <option value="Online Search">Online Search</option>
                        <option value="Others">Others</option>
                      </select>
                    </div>

                    <div className="col-md-12 mt-3 text-start">
                      <input
                        type="checkbox"
                        name="termsAccepted"
                        id="termsAccepted"
                        checked={formData.termsAccepted}
                        onChange={handleCheckboxChange}
                        required
                      />
                      <label htmlFor="termsAccepted" className="ms-2">
                        I agree to the <NavLink to="/terms_and_condition" onClick={() => {
                          window.scrollTo(0, 0);
                        }}>terms and conditions</NavLink>.
                      </label>
                    </div>

                    <div className="col-md-12 mt-4 recaptcha-container">
                      <ReCAPTCHA
                        sitekey={isLocalhost ? RECAPTCHA_TEST_SITE_KEY : RECAPTCHA_SITE_KEY}
                        onChange={handleRecaptchaChange}
                      />
                      {recaptchaError && (
                        <p style={{ color: 'red' }}>reCAPTCHA failed to load. Please try again.</p>
                      )}
                    </div>

                    <div className="col-md-12 col-xl-6">
                      <div className="form-button">
                        <button type="submit" className="form-btn btn-primary"disabled={isSubmitting}>
                        {isSubmitting ? "Sending..." : "Send Message"}
                        </button>
                      </div>
                    </div>

                    <p className='mt-3'><strong>What to Expect After Submitting Your Form?</strong></p>
                    <p className='mt-2'>1. A team member will call you at your preferred time to discuss your requirements in detail.</p>
                    <p>2. If possible, the next steps will be communicated immediately. If not, you will hear from us within 24-48 hours.</p>

                    <div className="col-md-12 col-lg-12 message-status">
                      {status && <p>{status}</p>}
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default Contact;
